import { Home, Package, Calendar, MoreHorizontal, Mic, Bug, BarChart3, X } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { useAssistantContext } from "@/contexts/AssistantContext";
import { useCurrentPageTitle } from "@/contexts/PageTitleContext";
import { useOnboardingGate } from "@/components/routing/OnboardingGate";
import { MoreSheet } from "./MoreSheet";

interface DockItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  to?: string;
  onClick?: () => void;
  /** Responsive display class controlling on which breakpoints the item shows. */
  showClass?: string;
}

export function MobileDock() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { togglePanel } = useAssistantContext();
  const { hideMobileDock } = useCurrentPageTitle();
  const { active: onboardingActive } = useOnboardingGate();
  const [moreOpen, setMoreOpen] = useState(false);

  if (hideMobileDock) return null;
  // While first-admin onboarding is in progress, hide the floating dock so
  // users can't bypass the guided setup.
  if (onboardingActive) return null;

  const onAssistant = location.pathname.startsWith("/advanced");
  if (onAssistant) return null;

  const isActive = (to?: string) =>
    !!to && (location.pathname === to || location.pathname.startsWith(to + "/"));

  // Mobile keeps the original 4-item layout (Panel · Planificador · [mic] · Inventario · Más).
  // Tablet (md+) widens the dock and adds Inventario to the left, plus Plagas + Analíticas
  // to the right — so the dock reads Panel · Planificador · Inventario · [mic] · Plagas · Analíticas · Más.
  const left: DockItem[] = [
    { key: "home", label: t("nav.panel", "Panel"), icon: Home, to: "/dashboard" },
    { key: "planner", label: t("nav.planner"), icon: Calendar, to: "/planner" },
    { key: "inventory-tablet", label: t("nav.inventory"), icon: Package, to: "/inventory", showClass: "hidden md:flex" },
  ];
  const right: DockItem[] = [
    { key: "inventory-mobile", label: t("nav.inventory"), icon: Package, to: "/inventory", showClass: "flex md:hidden" },
    { key: "pests", label: t("nav.pests"), icon: Bug, to: "/pests", showClass: "hidden md:flex" },
    { key: "analytics", label: t("nav.analytics"), icon: BarChart3, to: "/analytics", showClass: "hidden md:flex" },
    { key: "more", label: t("nav.more"), icon: MoreHorizontal, onClick: () => setMoreOpen((o) => !o) },
  ];

  const renderItem = (item: DockItem) => {
    const active = isActive(item.to) || (item.key === "more" && moreOpen);
    const Icon = item.key === "more" && moreOpen ? X : item.icon;
    return (
      <button
        key={item.key}
        onClick={() => (item.to ? navigate(item.to) : item.onClick?.())}
        className={cn(
          "flex-col items-center justify-center gap-[3px] flex-1 h-full rounded-2xl transition-colors",
          item.showClass ?? "flex",
          active ? "text-foreground" : "text-foreground/65"
        )}
      >
        <Icon className={cn("w-[22px] h-[22px]", active && "stroke-[2.5]")} />
        <span className={cn("text-[10px] font-medium leading-none", active && "font-semibold")}>{item.label}</span>
      </button>
    );
  };

  return (
    <>
      <div
              data-app-dock
        className="lg:hidden fixed left-0 right-0 z-[45] pointer-events-none"
        style={{ bottom: "var(--dock-bottom)" }}
      >
        <div className="mx-auto max-w-md md:max-w-3xl px-[14px]">
          <div
            className="pointer-events-auto relative h-16 rounded-[24px] bg-white/[0.92] backdrop-blur-[16px] border border-[#E5E9F2] shadow-[0_12px_34px_rgba(20,60,100,0.16)] flex items-stretch px-2 md:px-3 md:gap-1.5"
          >
            {left.map(renderItem)}
            {!onAssistant && <div className="w-16 flex-shrink-0" aria-hidden />}
            {right.map(renderItem)}

            {/* Center mic (hidden on Ulture Assistant page) */}
            {!onAssistant && (
              <button
                onClick={togglePanel}
                aria-label={t("assistant.openMic", "Voice assistant")}
                className="absolute left-1/2 -translate-x-1/2 -top-[22px] w-[58px] h-[58px] rounded-full border-[3px] border-white shadow-[0_10px_24px_rgba(31,125,200,0.5)] flex items-center justify-center active:scale-95 transition-transform"
                style={{ background: "var(--cta)" }}
              >
                <Mic className="w-[25px] h-[25px] text-white" />
              </button>
            )}
          </div>
        </div>
      </div>

      <MoreSheet open={moreOpen} onOpenChange={setMoreOpen} />
    </>
  );
}
